import { create } from 'zustand';

/**
 * Transient UI state for the "Authenticate Source" dialog.
 *
 * The dialog drives a small state machine: idle → connecting → (dismissed on
 * success | error | device lockout). `connecting` doubles as the disable latch
 * for the "Authenticate and Continue" button, so every terminal outcome MUST
 * clear it — otherwise the dialog wedges: it renders the failure but the only
 * remaining action is Cancel, and the stale latch survives into the next time
 * the dialog is opened (the store is module-scoped, so it outlives the
 * component).
 *
 * `rawPending` is NOT presentation. It is the ownership latch for an unresolved
 * camera/cloud write. Closing the dialog, clearing an error, or clearing a
 * lockout must NOT release it: the operator can dismiss while details→cloud IO
 * is still in flight, and releasing the latch there would authorize a second
 * write against the same attempt (ENG-4719 B4).
 *
 * Per rule: think-in-systems (ai.knowledge/global) — presentation and ownership
 * are different machines living in one store so hooks.ts can keep importing
 * this module (main / ENG-4283 extraction) without losing the latch.
 */
export type AddSourceAuthLockoutStatus = {
  isLocked: boolean;
  unlockTimeSeconds: number;
};

type AddSourceAuthStore = {
  show: boolean;
  error: string | null;
  connecting: string | null;
  rawPending: boolean;
  lockoutStatus: AddSourceAuthLockoutStatus | null;
  setShow: (show: boolean) => void;
  setError: (error: string | null) => void;
  setConnecting: (connecting: string | null) => void;
  setRawPending: (rawPending: boolean) => void;
  setLockoutStatus: (status: AddSourceAuthLockoutStatus | null) => void;
};

export const useAddSourceAuthStore = create<AddSourceAuthStore>()((set) => ({
  // state...
  show: false,
  error: null,
  connecting: null,
  rawPending: false,
  lockoutStatus: null,
  // actions...
  // Closing discards presentation so a re-open starts idle. rawPending is
  // owned by the in-flight attempt, not the dialog visibility bit.
  setShow: (show) =>
    set(show ? { show } : { show, error: null, connecting: null, lockoutStatus: null }),
  // A surfaced error ends the attempt's presentation. Clearing the error
  // (start of a retry) must NOT unlatch, since the retry sets `connecting`.
  setError: (error) => set(error === null ? { error } : { error, connecting: null }),
  setConnecting: (connecting) => set({ connecting }),
  setRawPending: (rawPending) => set({ rawPending }),
  // A locked device ends presentation too — the dialog counts down and the
  // user retries once the lockout expires.
  setLockoutStatus: (status) =>
    set(status?.isLocked ? { lockoutStatus: status, connecting: null } : { lockoutStatus: status })
}));
